/**
 * THIS PHONE'S OWN KEY (30 Oct). Owner: drivers must not be able to use a
 * different phone, period.
 *
 * The sign-in passkey is copied by iCloud Keychain / Google Password Manager
 * to every phone on the driver's account, so on its own it does not tie a
 * driver to one phone. This key is made here, inside this phone's browser,
 * marked non-extractable: the browser will sign with it but never hand it out,
 * and nothing syncs it anywhere. Every sign-in proves it, and the office holds
 * any phone whose key is not the approved one.
 *
 * Kept in a database of its own ('jw-phone'), not the app's main one: a new
 * store there needs a version change, and that must never be able to block a
 * delivery from saving.
 */
const DB = 'jw-phone';
const STORE = 'k';
const ID = 'phone';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function get(): Promise<CryptoKeyPair | null> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const r = db.transaction(STORE, 'readonly').objectStore(STORE).get(ID);
    r.onsuccess = () => resolve((r.result as CryptoKeyPair | undefined) ?? null);
    r.onerror = () => reject(r.error);
  });
}

async function put(pair: CryptoKeyPair): Promise<void> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(pair, ID);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

let cached: Promise<CryptoKeyPair> | null = null;

/** The phone's key pair, made once. Asks the phone to keep this app's data. */
function phoneKey(): Promise<CryptoKeyPair> {
  cached ??= (async () => {
    try { await navigator.storage?.persist?.(); } catch { /* best effort */ }
    const have = await get();
    if (have) return have;
    const pair = await crypto.subtle.generateKey(
      { name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']);
    await put(pair);
    // Read back: the stored copy is the one every later sign-in will use.
    return (await get()) ?? pair;
  })();
  cached.catch(() => { cached = null; });
  return cached;
}

function b64url(buf: ArrayBuffer): string {
  let s = '';
  for (const b of new Uint8Array(buf)) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** What kind of phone this is. Compared with the approved phone at sign-in. */
function traits(): Record<string, unknown> {
  let gpu: string | null = null;
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    const ext = gl?.getExtension('WEBGL_debug_renderer_info');
    gpu = ext && gl ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : null;
  } catch { /* not available */ }
  const nav = navigator as Navigator & { deviceMemory?: number };
  return {
    ua: navigator.userAgent,
    w: screen.width, h: screen.height, dpr: window.devicePixelRatio,
    cores: navigator.hardwareConcurrency ?? null, mem: nav.deviceMemory ?? null,
    touch: navigator.maxTouchPoints ?? null, gpu,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

export interface PhoneProof {
  jwk: JsonWebKey;
  sig: string;
  traits: Record<string, unknown>;
}

/** Sign `message` with this phone's key. */
export async function phoneProof(message: string): Promise<PhoneProof> {
  const pair = await phoneKey();
  const jwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
  const sig = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' }, pair.privateKey, new TextEncoder().encode(message));
  return {
    jwk: { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y },
    sig: b64url(sig),
    traits: traits(),
  };
}
